import { useState, useEffect } from 'react'
import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Checkbox } from '@/components/ui/checkbox'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { apiClient } from '@/lib/api-client'

interface ApiKey {
  id: string
  name: string
  key_prefix: string
  key: string
  permissions: string[]
  last_used_at: string | null
  expires_at: string | null
  created_at: string
}

interface CreateApiKeyDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onKeyCreated: (key: ApiKey) => void
}

const PERMISSIONS = [
  { value: 'read:networks', label: 'Read Networks' },
  { value: 'write:networks', label: 'Write Networks' },
  { value: 'trigger:validations', label: 'Trigger Validations' },
  { value: 'read:validations', label: 'Read Validations' },
  { value: 'read:jobs', label: 'Read Jobs' },
]

export function CreateApiKeyDialog({ open, onOpenChange, onKeyCreated }: CreateApiKeyDialogProps) {
  const [keyName, setKeyName] = useState('')
  const [expiresIn, setExpiresIn] = useState('never')
  const [permissions, setPermissions] = useState<string[]>([
    'read:networks',
    'read:validations',
    'read:jobs',
    'trigger:validations'
  ])
  const [isCreating, setIsCreating] = useState(false)
  const [formError, setFormError] = useState<string | null>(null)

  useEffect(() => {
    if (!open) {
      setFormError(null)
    }
  }, [open])

  const handleCreate = async () => {
    if (formError) {
      setFormError(null)
    }

    if (!keyName.trim()) {
      setFormError('Enter a name before creating an API key.')
      return
    }

    setIsCreating(true)
    try {
      const expiresInDays = expiresIn === 'never' ? undefined : parseInt(expiresIn)

      const response = await apiClient.createApiKey({
        name: keyName,
        permissions,
        expires_in_days: expiresInDays
      })

      if (response.success && response.data) {
        onKeyCreated({ ...response.data, last_used_at: null } as ApiKey)
        // Reset form
        setKeyName('')
        setExpiresIn('never')
        setPermissions([
          'read:networks',
          'read:validations',
          'read:jobs',
          'trigger:validations'
        ])
        setFormError(null)
      } else {
        throw new Error(response.error || 'Failed to create API key')
      }
    } catch (error) {
      setFormError(
        error instanceof Error ? error.message : 'Failed to create API key.'
      )
    } finally {
      setIsCreating(false)
    }
  }

  const togglePermission = (permission: string) => {
    setPermissions(prev =>
      prev.includes(permission)
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    )
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>Create API Key</DialogTitle>
          <DialogDescription>
            Create a new API key for GitHub Actions or CI/CD integration
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="key-name">Key Name</Label>
            <Input
              id="key-name"
              value={keyName}
              onChange={(e) => setKeyName(e.target.value)}
              placeholder="e.g., GitHub Actions Production"
              disabled={isCreating}
            />
            <p className="text-xs text-muted-foreground">
              A descriptive name to help you identify this key
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="expires">Expiration</Label>
            <Select value={expiresIn} onValueChange={setExpiresIn} disabled={isCreating}>
              <SelectTrigger id="expires">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="never">Never expires</SelectItem>
                <SelectItem value="30">30 days</SelectItem>
                <SelectItem value="60">60 days</SelectItem>
                <SelectItem value="90">90 days</SelectItem>
                <SelectItem value="365">1 year</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-2">
            <Label>Permissions</Label>
            <div className="space-y-2 border rounded-lg p-3">
              {PERMISSIONS.map((perm) => (
                <div key={perm.value} className="flex items-center space-x-2">
                  <Checkbox
                    id={perm.value}
                    checked={permissions.includes(perm.value)}
                    onCheckedChange={() => togglePermission(perm.value)}
                    disabled={isCreating}
                  />
                  <Label
                    htmlFor={perm.value}
                    className="text-sm font-normal cursor-pointer"
                  >
                    {perm.label}
                  </Label>
                </div>
              ))}
            </div>
          </div>
        </div>

        {formError && (
          <Alert variant="destructive">
            <AlertTitle>Unable to create API key</AlertTitle>
            <AlertDescription>{formError}</AlertDescription>
          </Alert>
        )}

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            disabled={isCreating}
          >
            Cancel
          </Button>
          <Button
            onClick={handleCreate}
            disabled={isCreating || !keyName.trim() || permissions.length === 0}
          >
            {isCreating ? 'Creating...' : 'Create API Key'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
